/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this source code and do not have permission, please contact the copyright holders and delete this file.
 */

import { parsePhoneNumber } from 'libphonenumber-js';
import { Injectable, inject, OnDestroy, signal, WritableSignal } from '@angular/core';
import { UserCredential } from '@angular/fire/auth';
import { OSKErrorService, OSKHttpsSuccessResponse } from '@oskey/core';
import {
  OSKCountry,
  OSKCreateUserDTO,
  OSKPhoneNumber,
  OSKUpdateProfileDTO,
  OSKUser,
  OSKUserUpdatesPublicProfileRequest
} from '@oskey/core/types';
import { OSKFirebaseAuthService, OSKFirebaseHttpsService } from '@oskey/firebase';
import { AuthService as Auth0Service, User as Auth0User } from '@auth0/auth0-angular';
import {
  Observable,
  of,
  Subscription,
  combineLatest,
  from,
  map,
  firstValueFrom,
  tap,
  finalize
} from 'rxjs';
import { filter, switchMap, take, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  OSKOrganizationPMPUserInvitation,
  OSKOrganizationProcessPMPInvitationRequest
} from '../../portals/organization/features/entities/features/entity/features/properties/features/users/types/organization-user.type';

@Injectable({
  providedIn: 'root'
})
export class OSKAuthService implements OnDestroy {
  private readonly firebase: OSKFirebaseAuthService = inject(OSKFirebaseAuthService);
  private readonly firebaseHttps: OSKFirebaseHttpsService = inject(OSKFirebaseHttpsService);
  private readonly auth0 = inject(Auth0Service);
  private readonly router = inject(Router);
  private readonly errorService: OSKErrorService = inject(OSKErrorService);

  private authSubscription: Subscription;

  public readonly isAuthenticated$: Observable<boolean> = this.auth0.isAuthenticated$;
  public readonly user$: Observable<Auth0User | null | undefined> = this.auth0.user$;
  public readonly isLoading: WritableSignal<boolean> = signal(false);

  constructor() {
    console.log('OSKAuthService constructor: Setting up Auth0 to Firebase token exchange.');
    this.authSubscription = this.auth0.idTokenClaims$
      .pipe(
        tap(() => this.isLoading.set(true)),
        filter((claims): claims is { __raw: string } => {
          if (!claims) {
            this.isLoading.set(false);
            return false;
          }
          const hasClaims = !!claims;
          console.log('Step 1: Intercepting Auth0 ID token claims. Are claims present?', hasClaims);
          return hasClaims;
        }),
        take(1),
        switchMap((claims) => {
          console.log('Step 2: Auth0 token received. Exchanging for Firebase custom token.');
          return this.exchangeAuth0TokenForFirebaseToken(claims.__raw);
        }),
        switchMap((result) => {
          console.log(
            'this is the result of the callble : exchangeAuth0TokenForFirebaseToken ----> ',
            result
          );
          console.log('his is the result?.newUser ---->', result?.newUser);
          console.log(
            'his is the result?.newUserresult?.firebaseToken ---->',
            result?.firebaseToken
          );
          console.log('his is the result?.userId ---->', result?.userId);

          if (result?.newUser && result?.firebaseToken) {
            console.log('Step 3: New user detected. Creating Firebase account.');
            return combineLatest({
              userCredential: this.firebase.signInWithCustomToken(result.firebaseToken),
              auth0User: this.auth0.user$.pipe(take(1)),
              mfaPhone: this.getMfaPhoneNumber(result.userId)
            }).pipe(
              switchMap(({ userCredential, auth0User, mfaPhone }) => {
                console.log('this is the userCredential ---->', userCredential);
                console.log('this is the auth0User ---->', auth0User);
                console.log('this is the mfaPhone ---->', mfaPhone);
                if (userCredential && auth0User) {
                  console.log(
                    'Step 4: Firebase account created. Creating user profile in Firestore /users collection.'
                  );
                  console.log('userCredential.user.uid ---->', userCredential.user.uid);
                  console.log('auth0User.email ---->', auth0User.email);
                  console.log('auth0User.phone_number ---->', auth0User.phone_number);
                  console.log('auth0User.nickname ---->', auth0User.nickname);
                  const phoneNumberString = mfaPhone.data?.phoneNumber;
                  let phoneNumberDTO: OSKPhoneNumber | undefined;
                  if (phoneNumberString) {
                    const phoneNumber = parsePhoneNumber(phoneNumberString);
                    if (phoneNumber && phoneNumber.country) {
                      phoneNumberDTO = {
                        isoCountryCode: phoneNumber.country,
                        dialCode: `+${phoneNumber.countryCallingCode}`,
                        localPhoneNumber: phoneNumber.nationalNumber,
                        internationalPhoneNumber: phoneNumber.number
                      };
                    }
                  }

                  const userDTO: OSKCreateUserDTO = {
                    userId: userCredential.user.uid,
                    email: auth0User.email!,
                    ...(phoneNumberDTO && { phoneNumber: phoneNumberDTO }),
                    publicProfile: {
                      firstName: auth0User.given_name || '',
                      lastName: auth0User.family_name || ''
                    }
                  };

                  return from(this.createUser(userDTO)).pipe(
                    switchMap(() => from(userCredential.user.getIdToken(true))),
                    switchMap(() => {
                      return from(this.queryPMPInvitations()).pipe(
                        switchMap((invitationsRes) => {
                          const invitations = invitationsRes.data;
                          const invitation =
                            invitations && invitations.length > 0 ? invitations[0] : null;
                          if (invitation) {
                            console.log('Invitation found, updating profile with:', invitation);
                            const profileUpdate: OSKUserUpdatesPublicProfileRequest = {
                              publicProfile: {
                                userId: userCredential.user.uid,
                                firstName: invitation.firstName,
                                lastName: invitation.lastName
                              }
                            };
                            return from(this.updateUserProfile(profileUpdate)).pipe(
                              switchMap(() => {
                                console.log('Step 5: Processing PMP invitation.');
                                const processRequest: OSKOrganizationProcessPMPInvitationRequest = {
                                  organizationId: invitation.organizationId,
                                  email: auth0User.email!
                                };
                                return from(this.processPMPInvitation(processRequest)).pipe(
                                  map(() => userCredential)
                                );
                              }),
                              map(() => userCredential),
                              catchError((err) => {
                                this.errorService.showError(err);
                                return of(userCredential);
                              })
                            );
                          }
                          return of(userCredential);
                        })
                      );
                    })
                  );
                }
                return of(null);
              })
            );
          }
          if (result?.firebaseToken && !result.newUser) {
            console.log('Step 3: Firebase custom token received. Signing into Firebase.');
            return from(this.firebase.signInWithCustomToken(result.firebaseToken)).pipe(
              switchMap((userCredential) => {
                return from(this.queryPMPInvitations()).pipe(
                  map((invitationsRes) => {
                    const invitations = invitationsRes.data;
                    if (invitations && invitations.length > 0) {
                      return { ...userCredential, hasPendingInvitations: true };
                    }
                    return { ...userCredential, hasPendingInvitations: false };
                  })
                );
              })
            );
          }
          console.log('Step 3: No Firebase custom token received. Skipping Firebase sign-in.');
          return of(null);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: async (userCredential) => {
          const cred = userCredential as UserCredential & { hasPendingInvitations?: boolean };
          if (cred?.hasPendingInvitations) {
            console.log('Existing user has pending invitations. Redirecting to invitations page.');
            this.router.navigateByUrl('/user/organizations/invitations');
          } else if (cred) {
            console.log('Successfully signed in. Redirecting to organization entities.');
            await this.redirectToDefaultPortal(cred);
          }
        },
        error: (err) => console.error('Firebase custom sign-in failed', err)
      });
  }

  ngOnDestroy(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  signOut(): Promise<void> {
    return this.firebase.signOut().then(() => {
      // After Firebase sign-out, trigger Auth0 logout and redirect.
      const logout$ = this.auth0.logout({
        logoutParams: { returnTo: window.location.origin }
      });
      return firstValueFrom(logout$ || of(void 0));
    });
  }

  signUpWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    return this.firebase.signUpWithEmailAndPassword(email, password);
  }

  signInWithEmailAndPassword(email: string, password: string): Promise<UserCredential> {
    return this.firebase.signInWithEmailAndPassword(email, password);
  }

  verifyResetPasswordCode(code: string): Promise<string> {
    return this.firebase.verifyPasswordResetCode(code);
  }

  resetPassword(code: string, password: string): Promise<void> {
    return this.firebase.resetPassword(code, password);
  }

  signUpWithEmailLink(email: string, continueUrl?: string): Promise<boolean> {
    return this.firebase.signUpWithEmailLink(email, continueUrl);
  }

  confirmSignIn(): Promise<UserCredential | null> {
    return this.firebase.confirmSignIn();
  }

  createUser(userDTO: OSKCreateUserDTO): Promise<void> {
    return this.firebase.setDoc(userDTO, 'users', userDTO.userId);
  }

  updateProfile(profileDTO: OSKUpdateProfileDTO): Promise<void> {
    return this.firebase.updateProfile(profileDTO);
  }

  getUserByUid(uid: string): Promise<OSKUser | undefined> {
    return this.firebase.getUserByUid(uid);
  }

  sendResetPasswordEmail(email: string): Promise<void> {
    return this.firebase.sendPasswordResetEmail(email);
  }

  updateUserProfileAndPhoneNumber(
    data: OSKCreateUserDTO
  ): Promise<OSKHttpsSuccessResponse<undefined>> {
    return this.firebaseHttps.call<OSKCreateUserDTO, undefined>(
      'user-updateUserProfileAndPhoneNumber',
      data
    );
  }
  updateUserProfile(
    data: OSKUserUpdatesPublicProfileRequest
  ): Promise<OSKHttpsSuccessResponse<undefined>> {
    return this.firebaseHttps.call<OSKUserUpdatesPublicProfileRequest, undefined>(
      'user-updateUserProfileAndPhoneNumber',
      data
    );
  }

  public getCountriesNoAuth(): Promise<OSKHttpsSuccessResponse<OSKCountry[]>> {
    return this.firebaseHttps.call<never, OSKCountry[]>('core-getCountriesNoAuth');
  }

  // auth0 services

  loginWithRedirect(connection?: string): void {
    this.auth0.loginWithRedirect({ authorizationParams: { connection } });
  }
  exchangeAuth0TokenForFirebaseToken(
    token: string
  ): Promise<{ firebaseToken: string; newUser: boolean; userId: string } | null> {
    console.log('Calling "core-exchangeAuth0Token" cloud function with Auth0 token.');
    return this.firebaseHttps
      .call<
        { token: string },
        { firebaseToken: string; newUser: boolean; userId: string }
      >('core-exchangeAuth0Token', { token })
      .then((response) => response.data ?? null);
  }

  queryPMPInvitations(): Promise<OSKHttpsSuccessResponse<OSKOrganizationPMPUserInvitation[]>> {
    return this.firebaseHttps.call<never, OSKOrganizationPMPUserInvitation[]>(
      'organization-queryPMPInvitations'
    );
  }

  getMfaPhoneNumber(userId: string): Promise<OSKHttpsSuccessResponse<{ phoneNumber: string }>> {
    return this.firebaseHttps.call<{ userId: string }, { phoneNumber: string }>(
      'core-getMfaPhoneNumber',
      { userId }
    );
  }

  processPMPInvitation(
    data: OSKOrganizationProcessPMPInvitationRequest
  ): Promise<OSKHttpsSuccessResponse<{ message: string }>> {
    return this.firebaseHttps.call<{ organizationId: string; email: string }, { message: string }>(
      'organization-processPMPInvitation',
      data
    );
  }

  public async redirectToDefaultPortal(userCredential?: UserCredential | null): Promise<void> {
    const uid = userCredential?.user?.uid || this.firebase.currentFirebaseUser?.uid;
    if (uid) {
      try {
        const orgs = await this.firebase.getUserOrganizations(uid);
        if (orgs && orgs.length > 0 && orgs[0].organizationId) {
          console.log('Redirecting to user organization entities:', orgs[0].organizationId);
          await this.router.navigate(['/organization', orgs[0].organizationId, 'entities'], {
            queryParams: { redirectFirst: 'true' }
          });
          return;
        }

        try {
          const invitationsRes = await this.queryPMPInvitations();
          if (invitationsRes?.data && invitationsRes.data.length > 0) {
            console.log('User has pending invitations. Redirecting to invitations page.');
            await this.router.navigateByUrl('/user/organizations/invitations');
            return;
          }
        } catch (invErr) {
          console.warn('Could not query invitations for fallback:', invErr);
        }
      } catch (err) {
        console.warn('Could not fetch user organizations for redirect:', err);
      }
    }
    console.log('No organization found or fallback. Redirecting to account.');
    await this.router.navigateByUrl('/user/account');
  }
}
